(() => {
  const STORAGE_KEY = 'frangipani:wishlist:v1';
  const MAX_ITEMS = 100;
  const SELECTORS = {
    button: '[data-fr-wishlist-button]',
    open: '[data-fr-wishlist-open]',
    close: '[data-fr-wishlist-close]',
    drawer: '[data-fr-wishlist-drawer]',
    count: '[data-fr-wishlist-count]',
    drawerCount: '[data-fr-wishlist-drawer-count]',
    list: '[data-fr-wishlist-list]',
    empty: '[data-fr-wishlist-empty]',
    status: '[data-fr-wishlist-status]',
  };

  let wishlistStrings;
  const strings = () => {
    if (wishlistStrings) return wishlistStrings;
    const source = document.getElementById('FrWishlistStrings');
    if (!source) return {};
    try {
      wishlistStrings = JSON.parse(source.textContent);
    } catch (error) {
      return {};
    }
    return wishlistStrings;
  };

  const translated = (key, values = {}) => String(strings()[key] || '')
    .replace(/__(title|count|price)__/g, (token, name) => String(values[name] ?? token));

  const storeRoot = () => {
    const root = new URL(strings().routesRoot || window.Shopify?.routes?.root || '/', window.location.origin);
    if (root.origin !== window.location.origin) throw new Error('Unexpected store origin');
    if (!root.pathname.endsWith('/')) root.pathname += '/';
    return root;
  };

  const productUrlForHandle = (handle) => new URL(`products/${encodeURIComponent(handle)}`, storeRoot()).href;

  let closeTimer;
  let toastTimer;
  let lastFocusedElement;

  const announce = (message) => {
    const status = document.querySelector(SELECTORS.status);
    if (!status) return;

    status.textContent = '';
    window.setTimeout(() => {
      status.textContent = message;
    }, 0);
  };

  const showToast = (message) => {
    const toast = document.querySelector('[data-fr-wishlist-toast]');
    if (!toast) return;

    window.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.hidden = false;
    window.requestAnimationFrame(() => toast.classList.add('is-visible'));
    toastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => {
        if (!toast.classList.contains('is-visible')) toast.hidden = true;
      }, 180);
    }, 2200);
  };

  const notify = (message) => {
    announce(message);
    showToast(message);
  };

  const closestFromEvent = (event, selector) => {
    const directMatch = event.target instanceof Element ? event.target.closest(selector) : null;
    if (directMatch) return directMatch;

    const eventPath = typeof event.composedPath === 'function' ? event.composedPath() : [];
    return eventPath.find((node) => node instanceof Element && node.matches(selector)) || null;
  };

  const readItems = () => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(stored)) return [];

      const handles = new Set();
      return stored
        .filter((item) => item && typeof item.handle === 'string' && item.handle.trim())
        .filter((item) => {
          if (handles.has(item.handle)) return false;
          handles.add(item.handle);
          return true;
        })
        .slice(0, MAX_ITEMS)
        .map((item) => ({
          handle: String(item.handle),
          title: String(item.title || item.handle),
          url: String(item.url || productUrlForHandle(item.handle)),
          image: String(item.image || ''),
          price: String(item.price || ''),
        }));
    } catch (error) {
      return [];
    }
  };

  const writeItems = (items) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
    } catch (error) {
      return false;
    }

    document.dispatchEvent(new CustomEvent('frangipani:wishlist:change', { detail: { items } }));
    return true;
  };

  const parseProduct = (button) => {
    try {
      const product = JSON.parse(button.dataset.frWishlistProduct || '{}');
      if (!product.handle) return null;
      return {
        handle: String(product.handle),
        title: String(product.title || product.handle),
        url: String(product.url || productUrlForHandle(product.handle)),
        image: String(product.image || ''),
        price: String(product.price || ''),
      };
    } catch (error) {
      return null;
    }
  };

  const safeProductUrl = (url) => {
    try {
      const parsed = new URL(url, window.location.origin);
      if (parsed.origin !== window.location.origin) return '#';
      const productPath = parsed.pathname.match(/(?:^|\/)products\/[^/]+\/?$/);
      if (productPath) {
        const localized = new URL(productPath[0].replace(/^\//, ''), storeRoot());
        localized.search = parsed.search;
        localized.hash = parsed.hash;
        return localized.href;
      }
      return parsed.href;
    } catch (error) {
      return '#';
    }
  };

  const safeImageUrl = (url) => {
    try {
      const parsed = new URL(url, window.location.origin);
      return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : '';
    } catch (error) {
      return '';
    }
  };

  const updateInterface = (items = readItems()) => {
    const selectedHandles = new Set(items.map((item) => item.handle));

    document.querySelectorAll(SELECTORS.button).forEach((button) => {
      const product = parseProduct(button);
      if (!product) return;

      const selected = selectedHandles.has(product.handle);
      button.setAttribute('aria-pressed', String(selected));
      button.classList.toggle('is-active', selected);
      button.setAttribute(
        'aria-label',
        selected
          ? translated('removeProduct', { title: product.title })
          : translated('addProduct', { title: product.title }),
      );

      const label = button.querySelector('[data-fr-wishlist-button-label]');
      const nextLabel = translated(selected ? 'selected' : 'add');
      if (label && label.textContent !== nextLabel) label.textContent = nextLabel;
    });

    document.querySelectorAll(SELECTORS.count).forEach((count) => {
      const nextCount = String(items.length);
      if (count.textContent !== nextCount) count.textContent = nextCount;
      count.hidden = items.length === 0;
    });

    document.querySelectorAll(SELECTORS.drawerCount).forEach((count) => {
      const nextCount = String(items.length);
      if (count.textContent !== nextCount) count.textContent = nextCount;
    });

    document.querySelectorAll(SELECTORS.open).forEach((button) => {
      button.classList.toggle('is-active', items.length > 0);
      button.setAttribute(
        'aria-label',
        items.length > 0 ? translated('openCount', { count: items.length }) : translated('open'),
      );
    });
  };

  // Keep saved selections intact; current offer data is read-only and session-local.
  const wishlistCatalog = new Map();
  let wishlistRefresh;

  const syncWishlistRow = (article, handle) => {
    const state = wishlistCatalog.get(handle);
    const price = article.querySelector('.fr-wishlist-item__price');
    const link = article.querySelector('.fr-wishlist-item__link');
    if (price) {
      price.textContent = state?.status === 'ready'
        ? state.price
        : state?.status === 'unavailable'
          ? translated('unavailable')
          : state?.status === 'error'
            ? translated('availabilityError')
            : translated('checking');
    }
    if (state?.title) {
      const title = article.querySelector('.fr-wishlist-item__title');
      const remove = article.querySelector('[data-fr-wishlist-remove]');
      if (title) title.textContent = state.title;
      if (remove) remove.setAttribute('aria-label', translated('removeProduct', { title: state.title }));
    }
    if (link && state?.status === 'unavailable') {
      link.removeAttribute('href');
      link.setAttribute('aria-disabled', 'true');
    } else if (link && state?.status === 'ready') {
      link.href = safeProductUrl(state.url);
      link.removeAttribute('aria-disabled');
    }
  };

  const syncWishlistRows = () => {
    document.querySelectorAll('.fr-wishlist-item').forEach((article) => {
      const handle = article.querySelector('[data-fr-wishlist-remove]')?.dataset.frWishlistRemove;
      if (handle) syncWishlistRow(article, handle);
    });
  };

  const hydrateWishlist = (force = true) => {
    if (wishlistRefresh) return wishlistRefresh;
    const queue = readItems().map((item) => item.handle)
      .filter((handle) => force || !wishlistCatalog.has(handle));
    if (!queue.length) return Promise.resolve();

    queue.forEach((handle) => wishlistCatalog.set(handle, { status: 'loading' }));
    syncWishlistRows();

    const refreshProduct = async (handle) => {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 8000);
      try {
        const productUrl = new URL(productUrlForHandle(handle));
        const response = await fetch(`${productUrl.href}.js`, {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        if (response.status === 404) {
          wishlistCatalog.set(handle, { status: 'unavailable' });
          return;
        }
        if (!response.ok) throw new Error('Product refresh failed');
        const product = await response.json();
        if (product.available === false) {
          wishlistCatalog.set(handle, { status: 'unavailable', title: String(product.title || handle) });
          return;
        }
        if (product.available !== true || typeof product.price !== 'number'
          || !Number.isFinite(product.price) || product.price < 0) {
          throw new Error('Invalid product data');
        }
        const price = new Intl.NumberFormat(document.documentElement.lang || strings().locale || 'pl', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'PLN',
        }).format(product.price / 100);
        wishlistCatalog.set(handle, {
          status: 'ready',
          price: product.price_varies ? translated('fromPrice', { price }) : price,
          title: String(product.title || handle),
          url: product.url || productUrl.href,
        });
      } catch (error) {
        wishlistCatalog.set(handle, { status: 'error' });
      } finally {
        window.clearTimeout(timer);
        // Update existing nodes only: preserve focus and never restore removed rows.
        syncWishlistRows();
      }
    };

    wishlistRefresh = Promise.all(Array.from({ length: Math.min(4, queue.length) }, async () => {
      while (queue.length) await refreshProduct(queue.shift());
    })).finally(() => {
      wishlistRefresh = null;
      // A selection added in another tab during the request must also be checked.
      if (readItems().some((item) => !wishlistCatalog.has(item.handle))) {
        void hydrateWishlist(false);
      }
    });
    return wishlistRefresh;
  };

  const createItem = (item) => {
    const article = document.createElement('article');
    article.className = 'fr-wishlist-item';

    const link = document.createElement('a');
    link.className = 'fr-wishlist-item__link';
    link.href = safeProductUrl(item.url);

    const imageUrl = safeImageUrl(item.image);
    if (imageUrl) {
      const image = document.createElement('img');
      image.className = 'fr-wishlist-item__image';
      image.src = imageUrl;
      image.alt = '';
      image.loading = 'lazy';
      image.width = 160;
      image.height = 200;
      link.append(image);
    } else {
      const imagePlaceholder = document.createElement('span');
      imagePlaceholder.className = 'fr-wishlist-item__image fr-wishlist-item__image--placeholder';
      imagePlaceholder.setAttribute('aria-hidden', 'true');
      link.append(imagePlaceholder);
    }

    const copy = document.createElement('span');
    copy.className = 'fr-wishlist-item__copy';

    const title = document.createElement('span');
    title.className = 'fr-wishlist-item__title';
    title.textContent = item.title;
    copy.append(title);

    const price = document.createElement('span');
    price.className = 'fr-wishlist-item__price';
    copy.append(price);

    link.append(copy);
    article.append(link);

    const remove = document.createElement('button');
    remove.className = 'fr-wishlist-item__remove';
    remove.type = 'button';
    remove.dataset.frWishlistRemove = item.handle;
    remove.setAttribute('aria-label', translated('removeProduct', { title: item.title }));
    remove.textContent = translated('remove');
    article.append(remove);
    syncWishlistRow(article, item.handle);

    return article;
  };

  const renderDrawer = (items = readItems()) => {
    const list = document.querySelector(SELECTORS.list);
    const empty = document.querySelector(SELECTORS.empty);
    if (!list || !empty) return;

    list.replaceChildren(...items.map(createItem));
    empty.hidden = items.length > 0;
    list.hidden = items.length === 0;
    updateInterface(items);
  };

  const getDrawer = () => document.querySelector(SELECTORS.drawer);

  const openDrawer = (opener) => {
    const drawer = getDrawer();
    if (!drawer) return;

    window.clearTimeout(closeTimer);
    lastFocusedElement = opener || document.activeElement;
    renderDrawer();
    void hydrateWishlist();
    drawer.hidden = false;
    document.body.classList.add('fr-wishlist-lock');
    document.querySelectorAll(SELECTORS.open).forEach((button) => button.setAttribute('aria-expanded', 'true'));
    window.requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      drawer.querySelector('.fr-wishlist-drawer__panel')?.focus();
    });
  };

  const closeDrawer = () => {
    const drawer = getDrawer();
    if (!drawer || drawer.hidden) return;

    drawer.classList.remove('is-open');
    document.body.classList.remove('fr-wishlist-lock');
    document.querySelectorAll(SELECTORS.open).forEach((button) => button.setAttribute('aria-expanded', 'false'));
    closeTimer = window.setTimeout(() => {
      drawer.hidden = true;
      if (lastFocusedElement instanceof HTMLElement) lastFocusedElement.focus();
    }, 220);
  };

  const toggleProduct = (button) => {
    const product = parseProduct(button);
    if (!product) {
      notify(translated('readError'));
      return;
    }

    const items = readItems();
    const existingIndex = items.findIndex((item) => item.handle === product.handle);
    const isRemoving = existingIndex >= 0;
    if (isRemoving) {
      items.splice(existingIndex, 1);
    } else {
      items.unshift(product);
    }

    if (writeItems(items)) {
      updateInterface(items);
      if (!getDrawer()?.hidden) renderDrawer(items);
      notify(translated(isRemoving ? 'removedProduct' : 'addedProduct', { title: product.title }));
    } else {
      notify(translated('saveError'));
    }
  };

  const removeProduct = (handle, trigger) => {
    const currentItems = readItems();
    const removedIndex = currentItems.findIndex((item) => item.handle === handle);
    const removedItem = currentItems[removedIndex];
    const items = currentItems.filter((item) => item.handle !== handle);

    if (writeItems(items)) {
      renderDrawer(items);
      notify(translated('removedProduct', { title: wishlistCatalog.get(handle)?.title || removedItem?.title || translated('product') }));
      window.requestAnimationFrame(() => {
        const removeButtons = [...document.querySelectorAll('[data-fr-wishlist-remove]')];
        const nextButton = removeButtons[Math.min(Math.max(removedIndex, 0), removeButtons.length - 1)];
        const fallback = getDrawer()?.querySelector('[data-fr-wishlist-close]');
        (nextButton || fallback || trigger)?.focus();
      });
    } else {
      notify(translated('saveError'));
    }
  };

  // Handle product hearts during capture so card links or third-party scripts
  // cannot swallow the click before it reaches the wishlist.
  document.addEventListener(
    'click',
    (event) => {
      const wishlistButton = closestFromEvent(event, SELECTORS.button);
      if (!wishlistButton) return;

      event.preventDefault();
      event.stopPropagation();
      toggleProduct(wishlistButton);
    },
    { capture: true },
  );

  document.addEventListener('click', (event) => {
    const openButton = closestFromEvent(event, SELECTORS.open);
    if (openButton) {
      event.preventDefault();
      let focusReturnElement = openButton;
      const menuDrawer = openButton.closest('header-drawer');
      const menuSummary = menuDrawer?.querySelector('.menu-drawer-container > summary');
      if (menuDrawer?.closeMenuDrawer && menuSummary) {
        menuDrawer.closeMenuDrawer(event, menuSummary);
        focusReturnElement = menuSummary;
      }
      openDrawer(focusReturnElement);
      return;
    }

    if (closestFromEvent(event, SELECTORS.close)) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const removeButton = closestFromEvent(event, '[data-fr-wishlist-remove]');
    if (removeButton) {
      event.preventDefault();
      removeProduct(removeButton.dataset.frWishlistRemove, removeButton);
    }
  });

  document.addEventListener('keydown', (event) => {
    const drawer = getDrawer();
    if (!drawer || drawer.hidden) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = [...drawer.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hidden && element.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('frangipani:wishlist:change', (event) => {
    updateInterface(event.detail?.items || readItems());
  });
  document.addEventListener('shopify:section:load', () => updateInterface());
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) {
      const items = readItems();
      updateInterface(items);
      if (!getDrawer()?.hidden) renderDrawer(items);
      void hydrateWishlist();
    }
  });

  const initialize = () => {
    updateInterface();
    void hydrateWishlist();
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.addedNodes.length)) updateInterface();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
